import { useContext, useEffect, useState } from 'react';
import { PageTitleContext } from 'context/PageTitleContext';
import { CrumbsContext } from 'context/CrumbsContext';
import { ApiConsumer } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { ChevronDownIcon, ChevronUpIcon, DocumentDownloadIcon } from '@heroicons/react/outline';

const PER_PAGE = 20;

function formatDuration(seconds) {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function getMeta(file) {
  return file.user_defined || {};
}

export default function MusicBucket() {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [searchText, setSearchText] = useState('');
  const [downloading, setDownloading] = useState(false);

  let { setTitle } = useContext(PageTitleContext);
  let { setCrumbs } = useContext(CrumbsContext);

  useEffect(() => {
    setTitle('DBS Metadata');
    setCrumbs([
      { name: 'Library', link: '/library' },
      { name: 'DBS Metadata', link: '/music-bucket' },
    ]);
  }, []);

  const [searchTrigger, setSearchTrigger] = useState(0);

  useEffect(() => {
    setLoading(true);
    setExpandedRow(null);

    const params = { page, per_page: PER_PAGE };
    if (dateStart) params.date_start = dateStart;
    if (dateEnd) params.date_end = dateEnd;
    if (searchText.trim()) params.q = searchText.trim();

    ApiConsumer.get(API_ROUTES.LIBRARY.BUCKET.FILES, { params })
      .then((res) => {
        const data = res.data;
        setFiles(data.data || []);
        setTotalPages(data.total_pages || null);
        setHasNext(data.has_next || false);
        setTotalFiles(data.total || 0);
      })
      .catch((err) => console.error('Bucket files error:', err))
      .finally(() => setLoading(false));
  }, [page, searchTrigger]);

  function handleSearch() {
    if (page === 1) {
      setSearchTrigger((prev) => prev + 1);
    } else {
      setPage(1);
    }
  }

  function handleClear() {
    setDateStart('');
    setDateEnd('');
    setSearchText('');
    if (page === 1) {
      setSearchTrigger((prev) => prev + 1);
    } else {
      setPage(1);
    }
  }

  function handleDownload() {
    setDownloading(true);

    // Export respects the current search/date filters. With no filters set,
    // this downloads the whole table.
    const params = {};
    if (dateStart) params.date_start = dateStart;
    if (dateEnd) params.date_end = dateEnd;
    if (searchText.trim()) params.q = searchText.trim();

    ApiConsumer.get(API_ROUTES.LIBRARY.BUCKET.EXPORT, { params, responseType: 'blob' })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'DBS_Metadata_Report.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((err) => console.error('Bucket export error:', err))
      .finally(() => setDownloading(false));
  }

  function changePage(newPage) {
    if (newPage >= 1) {
      setPage(newPage);
    }
  }

  function renderPageNumbers() {
    if (totalPages) {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        pages.push(
          <button key={1} onClick={() => changePage(1)}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">1</button>
        );
        if (start > 2) pages.push(<span key="s1" className="px-1 text-gray-400">...</span>);
      }

      for (let i = start; i <= end; i++) {
        pages.push(
          <button key={i} onClick={() => changePage(i)}
            className={`px-3 py-1 text-sm border rounded ${
              i === page
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}>{i}</button>
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push(<span key="s2" className="px-1 text-gray-400">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => changePage(totalPages)}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">{totalPages}</button>
        );
      }

      return pages;
    }

    return (
      <span className="text-sm text-gray-700">Page {page}</span>
    );
  }

  return (
    <div className="h-full px-5 py-5 mt-0 mb-20">
      {/* Search Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Title, performer or composer..."
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            Search
          </button>
          <button
            onClick={handleClear}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            onClick={handleDownload}
            disabled={loading || downloading || totalFiles === 0}
            title="Download the current results (or the whole table when no filters are set) as Excel"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentDownloadIcon className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
            {downloading ? 'Preparing...' : 'Download'}
          </button>
        </div>
      </div>

      {totalFiles != null && (
        <div className="mb-3 text-sm text-gray-600">
          <span className="font-medium">{totalFiles.toLocaleString()}</span> record{totalFiles !== 1 ? 's' : ''} found
        </div>
      )}

      <div className="relative bg-white shadow overflow-hidden rounded-lg overflow-x-auto">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium text-indigo-600">Loading...</span>
            </div>
          </div>
        )}
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 w-10"></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Title</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Artist</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Album</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Duration</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Composer</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Created At</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">State</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {files.length === 0 && !loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-400">
                  No files found
                </td>
              </tr>
            ) : (
              files.map((file, idx) => {
                const meta = getMeta(file);
                const isExpanded = expandedRow === idx;
                return (
                  <>
                    <tr
                      key={file.acr_id || idx}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedRow(isExpanded ? null : idx)}
                    >
                      <td className="px-3 py-3 text-sm text-gray-400">
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900 truncate" title={file.title || meta.song_title || ''}>
                        {file.title || meta.song_title || '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 truncate" title={meta.performer || ''}>
                        {meta.performer || '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 truncate" title={meta.album || ''}>
                        {meta.album || '-'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDuration(file.duration)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 truncate" title={meta.composers || ''}>
                        {meta.composers || '-'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                        {file.created_at || '-'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            file.state === 1
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {file.state === 1 ? 'Ready' : file.state || '-'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`meta-${idx}`} className="bg-gray-50">
                        <td colSpan="8" className="px-8 py-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                            Additional Metadata
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2 text-sm">
                            <div>
                              <span className="text-gray-400">ID: </span>
                              <span className="text-gray-700">{file.id || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">ACR ID: </span>
                              <span className="text-gray-700 font-mono text-xs">{file.acr_id || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Song Title: </span>
                              <span className="text-gray-700">{meta.song_title || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Record Number: </span>
                              <span className="text-gray-700">{meta.record_number || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Company: </span>
                              <span className="text-gray-700">{meta.company || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">ISRC: </span>
                              <span className="text-gray-700">{meta.ISRC || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">CDID: </span>
                              <span className="text-gray-700">{meta.cdid || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Lyrics By: </span>
                              <span className="text-gray-700">{meta.lyrics_by || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Duration (meta): </span>
                              <span className="text-gray-700">{meta.duration || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Bucket ID: </span>
                              <span className="text-gray-700">{file.bucket_id || '-'}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {files.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <button
            disabled={page <= 1 || loading}
            onClick={() => changePage(page - 1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            &lsaquo; Previous
          </button>
          <div className="flex items-center gap-1">{renderPageNumbers()}</div>
          <button
            disabled={!hasNext || loading}
            onClick={() => changePage(page + 1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next &rsaquo;
          </button>
        </div>
      )}
    </div>
  );
}
